import type {
    PaymentType,
    SalesTransaction,
    TransactionStatus,
} from '@/api/models';

/**
 * Page size used for both the initial query and every load-more
 * request. Matches the bos-api default; we send it explicitly so the
 * value is visible in the queryKey and future tweaks are easy.
 */
export const TRANSACTIONS_PAGE_SIZE = 20;

/**
 * Debounce window (ms) for the seller-search input. Tuned for the
 * usual one-word-name query — short enough to feel live, long enough
 * not to fire on every keystroke.
 */
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * Wire values for the optional `TransactionType` filter on
 * `/api/v1/sales/transactions`. `'All'` is the UI-only sentinel for
 * "no filter" — the page hook strips it before passing to the query.
 */
export type TransactionTypeFilter = 'All' | 'Cash' | 'Cashless';

/**
 * Render order for the Segment in the View.
 */
export const TRANSACTION_TYPE_FILTERS: ReadonlyArray<TransactionTypeFilter> = [
    'All',
    'Cash',
    'Cashless',
];

/**
 * i18n key (relative to the `pages` namespace) for each filter
 * option's label.
 */
export const TRANSACTION_TYPE_FILTER_LABEL_KEY: Record<
    TransactionTypeFilter,
    string
> = {
    All: 'sales.widgets.transactions.types.all',
    Cash: 'sales.widgets.transactions.types.cash',
    Cashless: 'sales.widgets.transactions.types.cashless',
};

/** Stable column ids — shared between the View columns and sort descriptor. */
export const TRANSACTION_COLUMN = {
    txn: 'txn',
    seller: 'seller',
    status: 'status',
    payment: 'payment',
    total: 'total',
    actions: 'actions',
} as const;

/**
 * Row-level actions surfaced in the table's pinned actions dropdown.
 * Today the detail modal is read-only, so `details` is the sole action;
 * the array keeps the same shape as the Categories/Suppliers tables so
 * mutating actions can be slotted in later without touching the View.
 */
export type TransactionRowAction = 'details';

export const TRANSACTION_ROW_ACTIONS: {
    id: TransactionRowAction;
    icon: string;
    labelKey: string;
    variant?: 'danger';
}[] = [
    {
        id: 'details',
        icon: 'solar:eye-bold-duotone',
        labelKey: 'sales.widgets.transactions.actions.details',
    },
];

/**
 * Total item count for a transaction — sum of `quantity` across every
 * cart line. The mockup labels this "units" / "ერთეული"; for
 * `[Cola × 2, Chocolate × 1]` it reads `3`.
 */
export const getCartUnitsCount = (transaction: SalesTransaction): number => {
    return transaction.cart.reduce((sum, item) => sum + item.quantity, 0);
};

/**
 * Picks the payment type that contributed the largest amount to the
 * transaction's total — the one we surface in the row's payment cell
 * when a sale combines multiple methods. Returns `null` if the
 * `payments` array is empty.
 */
export const getDominantPaymentType = (
    transaction: SalesTransaction,
): PaymentType | null => {
    let best: PaymentType | null = null;
    let bestAmount = -Infinity;
    for (const payment of transaction.payments) {
        if (payment.amount > bestAmount) {
            bestAmount = payment.amount;
            best = payment.type;
        }
    }
    return best;
};

/**
 * True when at least two distinct payment types contributed to the
 * transaction (e.g. split between Cash and Card) — used to flag the
 * row's payment chip as "mixed" for the secondary methods.
 */
export const isMixedPayment = (transaction: SalesTransaction): boolean => {
    const seen = new Set<PaymentType>();
    for (const payment of transaction.payments) {
        seen.add(payment.type);
        if (seen.size > 1) {
            return true;
        }
    }
    return false;
};

/**
 * Map of payment type → Iconify identifier. Solar pack matches the
 * rest of the Sales widgets' icon vocabulary.
 */
export const PAYMENT_TYPE_ICON: Record<PaymentType, string> = {
    Card: 'solar:card-bold-duotone',
    Cash: 'solar:money-bag-bold-duotone',
    Invoice: 'solar:bill-list-bold-duotone',
    Voucher: 'solar:ticket-bold-duotone',
};

/**
 * Map of payment type → Tailwind colour class. Pairs the icon and the
 * label so the row's payment cell colour-codes by tender.
 */
export const PAYMENT_TYPE_COLOR: Record<PaymentType, string> = {
    Card: 'text-accent',
    Cash: 'text-success-soft-foreground',
    Invoice: 'text-warning-soft-foreground',
    Voucher: 'text-default-700',
};

/**
 * Maps a transaction's status (and refund presence) to a chip variant
 * the View renders without per-row branching. A refunded transaction
 * always wins — the status badge reads "refund" regardless of the
 * underlying `status` field.
 */
export type StatusBadgeKind = 'done' | 'refund' | 'pending' | 'canceled';

export const getStatusBadgeKind = (
    transaction: SalesTransaction,
): StatusBadgeKind => {
    if (transaction.refund) {
        return 'refund';
    }
    switch (transaction.status) {
        case 'Done':
            return 'done';
        case 'Pending':
        case 'Draft':
            return 'pending';
        case 'Canceled':
            return 'canceled';
        default:
            // Exhaustiveness fallback — unknown statuses render as the
            // safest "pending" while we add their explicit handling.
            return 'pending';
    }
};

/**
 * i18n key (relative to the `pages` namespace) for the status badge.
 * Keyed by the resolved badge kind so the View renders text only.
 */
export const STATUS_BADGE_LABEL_KEY: Record<StatusBadgeKind, string> = {
    canceled: 'sales.widgets.transactions.status.canceled',
    done: 'sales.widgets.transactions.status.done',
    pending: 'sales.widgets.transactions.status.pending',
    refund: 'sales.widgets.transactions.status.refund',
};

/**
 * i18n key (relative to the `pages` namespace) for each payment type's
 * label, e.g. row cell + modal stat tile.
 */
export const PAYMENT_TYPE_LABEL_KEY: Record<PaymentType, string> = {
    Card: 'sales.widgets.transactions.payments.card',
    Cash: 'sales.widgets.transactions.payments.cash',
    Invoice: 'sales.widgets.transactions.payments.invoice',
    Voucher: 'sales.widgets.transactions.payments.voucher',
};

/**
 * Friendly TXN id for the row + modal — strips the long opaque server
 * id and shows the trailing identifier the cashier will recognise
 * (e.g. `TNX_ID` → `TNX_ID`, `TNX_002` → `TNX_002`). Today this is a
 * passthrough; kept here so future formatting tweaks live in one place.
 */
export const formatTransactionId = (id: string): string => id;

/**
 * Locale ordinals for the `Done`/`Pending`/`Draft`/`Canceled` enum so
 * the V (transaction-status) cell in the View can use a single map
 * without branching on `status`.
 */
export const TRANSACTION_STATUS_LABEL_KEY: Record<TransactionStatus, string> = {
    Canceled: 'sales.widgets.transactions.status.canceled',
    Done: 'sales.widgets.transactions.status.done',
    Draft: 'sales.widgets.transactions.status.draft',
    Pending: 'sales.widgets.transactions.status.pending',
};

/** Server discount totals are authoritative; zero rows have no mark. */
export const isDiscountedTransaction = (
    transaction: SalesTransaction,
): boolean => transaction.discountTotalTetri > 0;

/** Semantic colours shared by receipt payment indicators. */
export const PAYMENT_CHIP_COLOR = {
    Card: 'accent',
    Cash: 'success',
    Invoice: 'default',
    Voucher: 'default',
} as const satisfies Record<PaymentType, 'accent' | 'success' | 'default'>;
